import hall from "@/assets/sim/sim-hall-wide.jpg";
import icTemple from "@/assets/sim/icon-temple.png";
import { cn } from "@/lib/utils";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
export const roman = (n: number) => ROMAN[n - 1] ?? String(n);

/** Learn-style card: Roman numeral medallion, serif title, dashed status ring, optional progress. */
export function NumeralCard({ n, title, subtitle, status, progress, progressLabel, tone = "default", children, className }: {
  n: number; title: string; subtitle?: string; status?: React.ReactNode; progress?: number; progressLabel?: string;
  tone?: "default" | "active" | "repair" | "locked"; children?: React.ReactNode; className?: string;
}) {
  return (
    <section className={cn(
      "rounded-2xl border bg-card p-4 sm:p-5 shadow-sm",
      tone === "active" ? "border-gold" : tone === "repair" ? "border-destructive/60" : "border-gold/30",
      tone === "locked" && "opacity-60",
      className,
    )}>
      <div className="flex gap-3 sm:gap-4">
        <div className="grid h-14 w-14 sm:h-16 sm:w-16 shrink-0 place-items-center rounded-full border-2 border-gold/60 font-serif text-2xl sm:text-3xl text-gold-dark">
          {roman(n)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="font-serif text-lg sm:text-xl font-semibold leading-tight text-foreground">{title}</h2>
              {subtitle && <p className="mt-1 font-serif text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{subtitle}</p>}
            </div>
            {status && (
              <div className="grid h-12 w-12 sm:h-14 sm:w-14 shrink-0 place-items-center rounded-full border border-dashed border-gold text-center font-serif text-[9px] sm:text-[10px] leading-tight text-gold-dark">
                {status}
              </div>
            )}
          </div>
          {progress != null && (
            <div className="mt-3 flex items-center gap-3">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full" style={{ width: `${Math.round(progress * 100)}%`, background: "var(--gradient-gold)" }} />
              </div>
              {progressLabel && <span className="shrink-0 text-xs text-muted-foreground">{progressLabel}</span>}
            </div>
          )}
        </div>
      </div>
      {children && <div className="mt-3 space-y-2 sm:pl-20">{children}</div>}
    </section>
  );
}

/** Boxed row inside a NumeralCard. */
export function NumeralRow({ icon, label, right, onClick }: { icon?: React.ReactNode; label: React.ReactNode; right?: React.ReactNode; onClick?: () => void }) {
  const Cmp: any = onClick ? "button" : "div";
  return (
    <Cmp onClick={onClick} className="flex w-full items-center gap-3 rounded-lg border border-border bg-background/60 px-3 py-2.5 text-left text-sm">
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="min-w-0 flex-1 text-foreground">{label}</span>
      {right != null && <span className="shrink-0 text-xs text-muted-foreground">{right}</span>}
    </Cmp>
  );
}

/** Dashed gold box (homework / habit / this week). */
export function DashedBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-gold/70 px-3 py-3">
      <p className="font-serif text-[10px] uppercase tracking-[0.3em] text-gold-dark">{label}</p>
      <div className="mt-1 text-sm text-foreground">{children}</div>
    </div>
  );
}

/** Centered hall banner with medallion, title and tagline. */
export function HallBanner({ title, tagline, icon = icTemple }: { title: string; tagline: string; icon?: string }) {
  return (
    <section className="relative overflow-hidden border-y-2 border-gold/50" style={{ height: "clamp(220px,36vw,320px)" }}>
      <img src={hall} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, hsl(var(--background) / 0.15), hsl(var(--background) / 0.55) 60%, hsl(var(--background) / 0.9))" }} />
      <div className="relative flex h-full flex-col items-center justify-center text-center px-4">
        <div className="grid h-20 w-20 sm:h-24 sm:w-24 place-items-center overflow-hidden rounded-full border-2 border-gold shadow-lg" style={{ background: "radial-gradient(circle, hsl(var(--bronze)), hsl(var(--bronze-dark)))" }}>
          <img src={icon} alt="" className="h-[80%] w-[80%] object-contain" />
        </div>
        <h1 className="mt-2 rounded-md border-2 border-gold px-5 py-1 font-serif text-2xl sm:text-4xl font-semibold uppercase tracking-[0.08em] text-gold-light shadow-lg" style={{ background: "linear-gradient(hsl(var(--bronze)), hsl(var(--bronze-dark)))" }}>{title}</h1>
        <span className="mt-1 flex items-center gap-2 text-gold"><span className="h-px w-10 bg-gold" />◆<span className="h-px w-10 bg-gold" /></span>
        <p className="mt-1 font-serif text-[10px] sm:text-xs uppercase tracking-[0.3em] text-foreground/80">{tagline}</p>
      </div>
    </section>
  );
}

